import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {

  constructor() { }

  /**
   * Export data to Excel file
   * @param data Array of objects to export
   * @param fileName Name of the file to be downloaded
   * @param sheetName Name of the sheet in Excel
   * @param headers Custom headers for the Excel file (optional)
   */
  exportToExcel(data: any[], fileName: string, sheetName: string = 'Sheet1', headers?: string[]): void {
    // If no data, return
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    try {
      let worksheet: XLSX.WorkSheet;

      if (headers && headers.length > 0) {
        // If custom headers are provided, create a worksheet with custom headers
        const worksheetData = this.processDataWithCustomHeaders(data, headers);
        worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      } else {
        // Use default json_to_sheet which uses object keys as headers
        worksheet = XLSX.utils.json_to_sheet(data);
      }

      // Create workbook and add the worksheet
      const workbook: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Generate filename with timestamp to avoid duplicates
      const exportFileName = `${fileName}_${new Date().getTime()}.xlsx`;

      // Write to file and trigger download
      XLSX.writeFile(workbook, exportFileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  }

  /**
   * Export MatTable data to Excel
   * @param tableData MatTable dataSource.data
   * @param displayedColumns Array of column names to include
   * @param columnLabels Map of column names to display labels
   * @param fileName Name of the file to be downloaded
   * @param sheetName Name of the sheet in Excel
   */
  exportMatTableData(
    tableData: any[],
    displayedColumns: string[],
    columnLabels: { [key: string]: string },
    fileName: string,
    sheetName: string = 'Sheet1'
  ): void {
    if (!tableData || tableData.length === 0 || !displayedColumns || displayedColumns.length === 0) {
      console.warn('No data or columns to export');
      return;
    }

    try {
      // Filter out action columns or columns that shouldn't be exported
      const exportableColumns = displayedColumns.filter(col =>
        col !== 'action' && col !== 'actions' && col !== 'select' && col !== 'img'
      );

      // Create formatted data for export
      const exportData = tableData.map(row => {
        const exportRow = {};
        exportableColumns.forEach(col => {
          // Use column label if provided, otherwise use the column name
          const headerName = columnLabels[col] || col;
          exportRow[headerName] = row[col];
        });
        return exportRow;
      });

      // Export the data
      this.exportToExcel(exportData, fileName, sheetName);
    } catch (error) {
      console.error('Error exporting MatTable data:', error);
    }
  }

  /**
   * Export card-style data to Excel
   * @param cardData Array of card data objects
   * @param mappingConfig Configuration for mapping card data to Excel columns
   * @param fileName Name of the file to be downloaded
   * @param sheetName Name of the sheet in Excel
   */
  exportCardData(
    cardData: any[],
    mappingConfig: { field: string, header: string }[],
    fileName: string,
    sheetName: string = 'Sheet1'
  ): void {
    if (!cardData || cardData.length === 0 || !mappingConfig || mappingConfig.length === 0) {
      console.warn('No card data or mapping configuration to export');
      return;
    }

    try {
      // Create formatted data for export
      const exportData = cardData.map(card => {
        const exportRow = {};
        mappingConfig.forEach(config => {
          exportRow[config.header] = card[config.field];
        });
        return exportRow;
      });

      // Export the data
      this.exportToExcel(exportData, fileName, sheetName);
    } catch (error) {
      console.error('Error exporting card data:', error);
    }
  }

  /**
   * Process data with custom headers for AOA format
   * @param data Array of objects to export
   * @param headers Custom headers for the Excel file
   * @returns Array of arrays format for XLSX
   */
  private processDataWithCustomHeaders(data: any[], headers: string[]): any[][] {
    // Create header row
    const result: any[][] = [headers];

    // Add data rows
    data.forEach(item => {
      const row: any[] = [];
      headers.forEach((header, index) => {
        // Find the corresponding property in the data object
        const keys = Object.keys(item);
        if (index < keys.length) {
          row.push(item[keys[index]]);
        } else {
          row.push(''); // Empty cell if no data
        }
      });
      result.push(row);
    });

    return result;
  }
}
